import { HttpContextToken, HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { MessageAlertService } from '@services/notifications/message-alert.service';
import { catchError, throwError } from 'rxjs';

/**
 * Opt out on a single request: `{ context: new HttpContext().set(SKIP_ERROR_TOAST, true) }`.
 *
 * For calls whose failure the screen already narrates — a polling tick, a
 * best-effort save — where a toast would be noise on top of the real message.
 */
export const SKIP_ERROR_TOAST = new HttpContextToken<boolean>(() => false);

/**
 * The safety net the app did not have.
 *
 * The only interceptor registered was the auth one, and it re-threw everything
 * that was not a 401 (`http-interceptor.ts`). So any `.subscribe()` written
 * without an `error` callback — and there are many — failed **completely
 * silently**: no toast, no log, and quite often a spinner left turning.
 *
 * It deliberately does NOT toast every status. 400 / 409 / 422 are the ones
 * screens do render inline (a field error, "el email ya existe"), and toasting
 * those too would double up on every form. What it covers is the class of
 * failure nobody writes a handler for:
 *
 * - `0`   — the network died or CORS refused; the request never reached us.
 * - `403` — forbidden; a screen that shows this inline is rare.
 * - `5xx` — ours, and always worth surfacing.
 *
 * The error is always re-thrown, so components that DO handle it keep working
 * exactly as before.
 */
export const ErrorToastInterceptor: HttpInterceptorFn = (request, next) => {
	// Both are resolved HERE, in the interceptor body: `inject()` only works
	// inside the injection context, and the `catchError` callback below runs
	// long after it has closed. They are root singletons, so this costs nothing.
	const alerts = inject(MessageAlertService);
	const transloco = inject(TranslocoService);

	return next(request).pipe(
		catchError((error: HttpErrorResponse) => {
			if (!request.context.get(SKIP_ERROR_TOAST) && shouldToast(error)) {
				alerts.showMessage('error', transloco.translate('errors.title'), messageFor(error, transloco));
			}
			return throwError(() => error);
		}),
	);
};

function shouldToast(error: HttpErrorResponse): boolean {
	if (!(error instanceof HttpErrorResponse)) return false;
	// El 404 entra el 12/09. No lo pintaba nadie —no está entre los que las
	// pantallas resuelven en línea, como el 400 o el 409— así que un «esto no
	// existe» se quedaba en silencio: al marcar un documento como verificado
	// salía un aspa roja SIN UNA PALABRA, y el fallo tardó semanas en verse.
	return error.status === 0 || error.status === 403 || error.status === 404 || error.status >= 500;
}

function messageFor(error: HttpErrorResponse, transloco: TranslocoService): string {
	if (error.status === 0) return transloco.translate('errors.networkError');
	if (error.status === 403) return transloco.translate('errors.forbidden');
	if (error.status === 404) return transloco.translate('errors.notFound');
	// The backend answers `{ statusCode, message }` and its 5xx message is
	// already generic ("Internal server error"), so prefer our translated copy.
	return transloco.translate('errors.serverError');
}
